import { Link } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { blogPublic, intake } from "../../lib/api";
import { withUtm } from "../../lib/utm";
import { useSeo } from "../../lib/useSeo";
import { Btn } from "../../lib/Btn";
import { Card } from "../../lib/Card";
import { Icon } from "../../lib/Icon";
import { Input } from "../../lib/Input";
import { Pill } from "../../lib/Pill";
import { Slats } from "../../lib/Slats";

export function BlogIndexPage() {
  useSeo({
    title: "Blog — NestPlot",
    description: "Self-storage operator playbooks, state-law explainers, and product updates from the NestPlot team.",
    path: "/blog",
  });
  const posts = useQuery({ queryKey: ["blog", "list"], queryFn: () => blogPublic.list(50) });
  const [email, setEmail] = useState("");
  const subscribe = useMutation({
    mutationFn: () => intake.subscribe(withUtm({ email, source: "blog-index", frequency: "weekly" as const })),
    onSuccess: () => {
      toast.success("Check your inbox to confirm");
      setEmail("");
    },
    onError: () => toast.error("Couldn't subscribe — try again"),
  });

  return (
    <div className="mx-auto max-w-[1080px] px-5 lg:px-8 py-12 lg:py-16">
      <div className="max-w-[640px]">
        <Pill tone="accent" size="sm">
          Building NestPlot in the open
        </Pill>
        <h1 className="t-display-xl mt-4">Blog</h1>
        <p className="text-sub text-lg mt-3">
          Notes from running a property operations company flat from $59/month — across
          multifamily, self-storage, parking, and RV/boat. Pricing AI experiments,
          state-law gotchas, migration playbooks, and the occasional rant about
          the enterprise vendors.
        </p>
      </div>

      <div className="mt-10 grid gap-4">
        {posts.isLoading && (
          <div className="text-center text-sub py-12">Loading posts…</div>
        )}
        {posts.isSuccess && posts.data.length === 0 && (
          <Card>
            <div className="px-6 py-10 text-center text-sub">
              First posts land any day. Subscribe below and we'll send them as they go up.
            </div>
          </Card>
        )}
        {posts.data?.map((post) => (
          <Link key={post.slug} to={`/blog/${post.slug}`}>
            <Card>
              {post.cover_image_url ? (
                <img
                  src={post.cover_image_url}
                  alt={post.title}
                  loading="lazy"
                  className="w-full h-[220px] object-cover"
                />
              ) : (
                <div className="bg-accent-soft px-5 pt-3 pb-2">
                  <Slats color="var(--accent)" opacity={0.7} height={10} />
                </div>
              )}
              <div className="px-6 py-5">
                <div className="flex items-center gap-2 mb-2">
                  <Pill tone="default" size="sm">
                    {post.post_type}
                  </Pill>
                  {post.published_at && (
                    <span className="text-sub text-xs font-mono">
                      {new Date(post.published_at).toLocaleDateString(undefined, {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })}
                    </span>
                  )}
                </div>
                <h2 className="font-display font-extrabold text-[22px] uppercase tracking-[-0.01em]">
                  {post.title}
                </h2>
                {post.excerpt && (
                  <p className="text-sub text-[15px] leading-relaxed mt-2">{post.excerpt}</p>
                )}
                <div className="mt-3 inline-flex items-center gap-1 text-accent text-[13px] font-bold uppercase tracking-[0.04em]">
                  Read more <Icon name="arrow-right" size={12} />
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <Card className="mt-10">
        <div className="bg-ink px-6 pt-3 pb-2">
          <Slats color="var(--accent)" opacity={0.7} height={10} />
        </div>
        <div className="px-6 py-6 grid md:grid-cols-[1.4fr_1fr] gap-6 items-center">
          <div>
            <div className="font-mono text-[11px] text-sub tracking-[0.14em] uppercase">
              Weekly digest
            </div>
            <h3 className="font-display font-black text-[26px] tracking-[-0.025em] mt-1">
              One email a week. No fluff.
            </h3>
            <p className="text-sub text-sm mt-2 max-w-[420px]">
              The week's posts, plus the one thing we shipped that operators should
              know about. Unsubscribe with one click.
            </p>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (email) subscribe.mutate();
            }}
            className="flex gap-2"
          >
            <Input
              type="email"
              placeholder="you@operator.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <Btn kind="primary" type="submit" size="md" disabled={subscribe.isPending || !email}>
              {subscribe.isPending ? "…" : "Subscribe"}
            </Btn>
          </form>
        </div>
      </Card>
    </div>
  );
}
